/**
 * Non-standard in-app browser (WebView) detection and external browser launcher.
 *
 * In-app browsers (KakaoTalk, Line, Instagram, Naver, etc.) restrict Google OAuth sign-in,
 * Web Push notifications, PWA installation, and WebRTC / audio permissions.
 *
 * This utility provides detection and deep-link / intent redirection to launch the device's
 * standard default web browser (Safari on iOS, Chrome / Samsung Internet on Android).
 */

export type InAppBrowserKind =
  | 'kakaotalk'
  | 'line'
  | 'naver'
  | 'daum'
  | 'facebook'
  | 'instagram'
  | 'twitter'
  | 'tiktok'
  | 'wechat'
  | 'android-webview'
  | 'ios-webview'
  | null;

/**
 * Detects whether the given user agent is an in-app browser / embedded webview.
 */
export function detectInAppBrowser(userAgent: string): InAppBrowserKind {
  const ua = userAgent ?? '';
  if (!ua) return null;

  if (/kakaotalk/i.test(ua)) return 'kakaotalk';
  if (/line\//i.test(ua)) return 'line';
  if (/naver\(inapp|naver/i.test(ua)) return 'naver';
  if (/daumapps|daumdevice/i.test(ua)) return 'daum';
  if (/instagram/i.test(ua)) return 'instagram';
  if (/fban|fbav|fbsv|fbios/i.test(ua)) return 'facebook';
  if (/twitter|tweetbot/i.test(ua)) return 'twitter';
  if (/micromessenger/i.test(ua)) return 'wechat';
  if (/musical_ly|bytedance|tiktok/i.test(ua)) return 'tiktok';

  // Generic Android WebView detection (marked by '; wv' or standalone 'wv')
  if (/android/i.test(ua) && /(; wv|\bwv\b)/i.test(ua)) {
    return 'android-webview';
  }

  // Generic iOS WKWebView / UIWebView detection
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const hasStandaloneIOSEngine =
    /Version\/.*Safari\//i.test(ua) ||
    /CriOS\//i.test(ua) ||
    /FxiOS\//i.test(ua) ||
    /EdgiOS\//i.test(ua);
  if (isIOS && /AppleWebKit/i.test(ua) && /Mobile\//i.test(ua) && !hasStandaloneIOSEngine) {
    return 'ios-webview';
  }

  return null;
}

/**
 * Convenient boolean check for whether the current environment is an in-app browser.
 * Installed standalone PWA instances are excluded from breakout checks.
 */
export function isInAppBrowser(userAgent: string): boolean {
  if (typeof window !== 'undefined') {
    const isStandalone =
      ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches);
    if (isStandalone) return false;
  }
  return detectInAppBrowser(userAgent) !== null;
}

/**
 * Generates an external/default browser deep link or intent scheme if supported.
 * Returns null if the browser/OS cannot be programmatically redirected (e.g. non-Kakao iOS WebViews).
 */
export function getExternalBrowserUrl(targetUrl: string, userAgent: string): string | null {
  const kind = detectInAppBrowser(userAgent);
  if (!kind) return null;

  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

  // Android: All in-app browsers (KakaoTalk, Line, Naver, generic WebViews) must use the Android Intent scheme.
  // Using kakaotalk://web/openExternal on Android fails/bugs out on modern builds.
  if (isAndroid) {
    const cleanUrl = targetUrl.replace(/^https?:\/\//i, '').replace(/#/g, '%23');
    return `intent://${cleanUrl}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`;
  }

  // iOS KakaoTalk: reliably opens default browser via KakaoTalk internal scheme
  if (isIOS && kind === 'kakaotalk') {
    return `kakaotalk://web/openExternal?url=${encodeURIComponent(targetUrl)}`;
  }

  // iOS Line: opens Safari when ?openExternalBrowser=1 is set
  if (isIOS && kind === 'line') {
    if (targetUrl.includes('openExternalBrowser=1')) {
      return null;
    }
    const separator = targetUrl.includes('?') ? '&' : '?';
    return `${targetUrl}${separator}openExternalBrowser=1`;
  }

  return null;
}

/**
 * Attempts to launch the system default external browser.
 * Returns true if an external URL was resolved and the navigate handler was invoked.
 */
export function openExternalBrowser(
  targetUrl: string,
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  navigate: (url: string) => void = (url: string) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    }
  },
): boolean {
  const externalUrl = getExternalBrowserUrl(targetUrl, userAgent);
  if (externalUrl) {
    navigate(externalUrl);
    return true;
  }
  return false;
}
